import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Gift } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GoogleButton from '../components/GoogleButton';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const sparklesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const createSparkle = () => {
      if (!sparklesRef.current) return;

      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';

      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);

      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;

      sparklesRef.current.appendChild(sparkle);

      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      setError('Password should be at least 6 characters');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await signUp(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to create account. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (value: string) => {
    setPassword(value);
    if (value.length > 0 && value.length < 6) {
      setError('Password should be at least 6 characters');
    } else {
      setError('');
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setError('');
      setGoogleLoading(true);
      await signInWithGoogle();
      // page redirects to Google — no need to reset loading
    } catch (err) {
      setError('Google sign-up failed. Please try again.');
      console.error(err);
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-pastel-gradient relative overflow-hidden">
      <div className="absolute inset-0 bg-confetti-pattern opacity-30"></div>

      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      {/* Logo */}
      <div className="relative z-10">
        <Link to="/" className="absolute top-8 left-8 group">
          <img src="/blue-no-logo.png" alt="Giftyfy" className="h-20" style={{ mixBlendMode: 'multiply' }} />
        </Link>
      </div>

      <div className="flex min-h-screen items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-md space-y-8 card-pastel p-10 animate-scale-in">
          <div className="text-center">
            <Gift className="w-16 h-16 mx-auto mb-6 text-pastel-blue-600" />
            <h2 className="text-3xl font-poppins font-bold text-gray-800 mb-2">Create Your Account</h2>
            <p className="text-gray-600 font-poppins">
              Already have an account?{' '}
              <Link to="/login" className="text-pastel-purple-600 hover:text-pastel-purple-700 font-medium transition-colors duration-300">
                Sign in
              </Link>
            </p>
          </div>

          {error && (
            <div className="bg-pastel-pink-100 border border-pastel-pink-300 text-pastel-pink-800 px-4 py-3 rounded-2xl animate-fade-in backdrop-blur-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <GoogleButton loading={googleLoading} onClick={handleGoogleSignUp} label="Sign up with Google" />

            <div className="relative flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-poppins whitespace-nowrap">or sign up with email</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-3.5 h-5 w-5 text-gray-600" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 input-pastel"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-3.5 h-5 w-5 text-gray-600" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => validatePassword(e.target.value)}
                  className="pl-12 pr-12 input-pastel"
                  placeholder="Create a password (min. 6 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600 transition-colors duration-300"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-pastel disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
